import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DynamicObjectInterface } from '../../../sdk/type';
import { useAPICall } from '../../../hooks';
import { LoggerService } from '../../../ui/utils';
import CampaignService from '../../../sdk/services/campaignService';
import AttachmentService from '../../../sdk/services/attachmentService';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import {
  AdminPageHeader,
  Button,
  ButtonStyles,
  Card,
  HeaderFlex /* TableContainer */,
  Theme,
  DropDown,
  MLPVersion,
} from '../../../ui/components';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import styles from './index.module.scss';

export interface BulkUploadPropsType {
  title: string;
  campaignType: 'CHALLENGE' | 'PROMOTION';
  name?:
    | 'coach-list'
    | 'coach-enrollment-master'
    | 'team-list'
    | 'inventory-list';
}

interface Campaign {
  title: string;
  value: string;
}

export const BulkUpload = (props: BulkUploadPropsType) => {
  const location = useLocation();
  const apiCall = useAPICall();
  const { adminToast } = useAdminToast();
  const { t } = useTranslation();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [campaignList, setCampaignList] = useState<DropDownData[]>([]);
  const [selectedCampaignValue, setSelectedCampaignValue] = useState<Campaign>(
    campaignList.length > 0
      ? { title: campaignList[0].title as string, value: campaignList[0].value as string }
      : { title: '', value: '' },
  );

  const fetchCampaignList = async () => {
    const result = await apiCall(CampaignService.getAllCampaigns);
    if (result.status === 200 && result?.data?.data?.length > 0) {
      let campaignData = result?.data?.data ?? [];
      setCampaignList(
        campaignData
          ?.filter((i: DynamicObjectInterface) => 
            i.campaignType === props.campaignType &&
            i.status !== 'DELETED' &&
            i.status !== 'CLOSED'
          )
          ?.map((d: DynamicObjectInterface) => ({
            title: `${d.campaignId} - ${d.campaignName}`,
            value: d.campaignId,
          })),
      );
    } else {
      LoggerService.log('Failed to retrieve coach list by campaign');
    }
  };

  useEffect(() => {
    fetchCampaignList();
  }, []);

  useEffect(() => {
    setSelectedFile(null);
    setSelectedCampaignValue({ title: '', value: '' });
  }, [location]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setSelectedFile(file);
      event.target.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLInputElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    setSelectedFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLInputElement>) => {
    event.preventDefault();
  };

  const handleUpload = async () => {
    if (selectedFile) {
      // You can perform upload action here, e.g., send file to server
      LoggerService.log('Uploading file:', selectedFile);

      const fileData = new FormData();
      fileData.append('file', selectedFile);

      const response = await apiCall(() =>
        AttachmentService.uploadBulkUpdateFile(props.name ?? '', fileData, selectedCampaignValue.value),
      );

      if (response.status === 200) {
        const contentType = response.headers['content-type'];

        if (contentType && contentType.includes('text/csv')) {
          const blob = await response.data;

          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.setAttribute('download', 'errorLogs.csv');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          adminToast({
            message: t('dashboard.api-message.common.errorOnFileUploadWithLogs'),
            theme: Theme.SUCCESS,
          });
        } else {
          adminToast({
            message: t('dashboard.api-message.common.successFileUpload'),
            theme: Theme.SUCCESS,
          });
        }
      } else {
        adminToast({
          message: t('dashboard.api-message.common.fileUploadError'),
          theme: Theme.ERROR,
        });
      }

      // Reset selected file
      setSelectedFile(null);
    }
  };
  return (
    <div className={styles['bulk-upload']}>
      <AdminPageHeader title={props.title} />
      {props.name === 'coach-list' && (
        <div className={styles['bulk-upload-rules']}>
          Rules: <br />
          1. Remove email hyperlink <br />
          2. Remove email underline <br />
          3. Remove spaces between digits in phone number <br />
          4. Remove multiple phone numbers
        </div>
      )}
      {props.name === 'coach-enrollment-master' && (
        <div className={styles['bulk-upload-rules']}>
          Rules: <br />
          1. Remove the VLOOKUP formula in Direct Leader Pin Column
          <br />
          (Steps: Select Direct Leader Pin Column {`>`} Ctrl + C {`>`} Ctrl + Alt + V {`>`} Select 'Values' )
        </div>
      )}
      {props.name === 'inventory-list' && (
        <div className={styles['bulk-upload-rules']}>
          Rules: <br />
          1. Download the Inventory Report from Report section <br />
          2. Update based on the downloaded excel report <br />
          3. Reupload the excel file <br />
          <br />
          (Caution: The updated list will replace the existing inventory )
        </div>
      )}
      <Card title="Upload File" headerFlex={HeaderFlex.CENTER} containerWidth="50%">
        <div className={styles['bulk-upload-dropdown']}>
          <DropDown
            name="campaignId"
            dropdownLabel={`${t('admin.teams.campaign')}`}
            disabledFilter={false}
            mlpVersion={MLPVersion.ONE}
            value={selectedCampaignValue.value}
            data={campaignList}
            configData={{
              label: 'title',
              onSelect: (selectedItem: DynamicObjectInterface) => {
                setSelectedCampaignValue(selectedItem);
              },
            }}
            readOnly={false}
            required={true}
          />
        </div>
        <div>
          <input
            id="fileInput"
            type="file"
            onChange={handleFileChange}
            className={styles['bulk-upload-input']}
            ref={fileInput => fileInput && (fileInput.onchange = handleFileChange as DynamicObjectInterface)}
            accept={['.xlsx', '.xls', '.csv'].join(', ')}
          />
          <Button
            buttonStyle={ButtonStyles.OUTLINE}
            title="Select File"
            onClick={() => document.getElementById('fileInput')?.click()}
          />
          <div onDrop={handleDrop} onDragOver={handleDragOver} className={styles['bulk-upload-drag-box']}>
            Drop file here
          </div>
          {selectedFile && <div style={{ marginTop: '10px' }}>Selected File: {selectedFile.name}</div>}
          <Button title="Upload" disabled={!selectedFile || !selectedCampaignValue.value} onClick={handleUpload} style={{ marginTop: '10px' }} />
        </div>
        {/* <TableContainer  /> */}
      </Card>
    </div>
  );
};
